import { useCallback, useMemo, useRef, useState } from 'react';
import {
  AnimatedPopover,
  Content,
  Inline,
  InlineItem,
  Pill,
  Strong,
  Text,
  UtilityButton,
  useEventListener,
  useOnClickOutside,
} from '@kinesis/bungle';
import { DownloadButton } from '@/components/download-button';
import { FileUploader } from '@/components/file-uploader';
import type { ScenarioMethodology } from '@/types';

// The uploader accepts PDFs only — village enforces the same at the bind
// route, this is the browse-dialog filter + the pill on the prompt.
const PDF_ONLY = [{ label: 'PDF', fileExtension: '.pdf' }];

// The "Methodology reference" section body: a single, replaceable PDF slot,
// rendered as ONE flat row whose geometry is shared by both states — empty
// (the empty-state line + an Upload button) and filled (a format pill, the
// filename, and Download / Replace / Remove) — so it reads as a slot that is
// either empty or holds the document, never a list. Upload and Replace open
// the popover uploader — an `AnimatedPopover` hosting the `FileUploader`
// (with its full-screen drop overlay); choosing a file closes the popover
// and hands the file up. Presentational: the page owns the transfer flow and
// the mutations.
const MethodologyReference = (props: MethodologyReferenceProps) => {
  const {
    methodology,
    busy = false,
    downloading = false,
    onUpload,
    onRemove,
    onDownload,
  } = props;

  const buttonRef = useRef<HTMLButtonElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((current) => !current), []);
  const close = useCallback(() => setIsOpen(false), []);

  useOnClickOutside(
    useMemo(() => [buttonRef, paneRef], []),
    close,
  );

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') close();
  });

  // A chosen file closes the popover and goes up; the page runs the
  // transfer flow (mint upload → PUT → bind).
  const handleFiles = useCallback(
    (files: FileList) => {
      const file = files[0];
      if (!file) return;
      close();
      onUpload?.(file);
    },
    [close, onUpload],
  );

  const uploadButton = (label: string) =>
    onUpload && (
      <UtilityButton
        ref={buttonRef}
        magnitude='small'
        icon='upload'
        disabled={busy}
        onClick={toggle}
      >
        {label}
      </UtilityButton>
    );

  return (
    <>
      <Content
        background='white'
        padding='medium'
        border
        paddingMode='equal'
        borderRadiusSize='small'
      >
        {methodology ? (
          <Inline space='small' alignY='center'>
            <InlineItem sizing='fit-content'>
              <Pill>{methodology.format.toUpperCase()}</Pill>
            </InlineItem>
            <InlineItem sizing='fill-container'>
              <Strong>{methodology.filename}</Strong>
            </InlineItem>
            {onDownload && (
              <InlineItem sizing='fit-content'>
                <DownloadButton
                  magnitude='small'
                  downloading={downloading}
                  onClick={onDownload}
                >
                  Download
                </DownloadButton>
              </InlineItem>
            )}
            {onUpload && (
              <InlineItem sizing='fit-content'>
                {uploadButton('Replace')}
              </InlineItem>
            )}
            {onRemove && (
              <InlineItem sizing='fit-content'>
                <UtilityButton
                  magnitude='small'
                  icon='delete'
                  disabled={busy}
                  onClick={onRemove}
                >
                  Remove
                </UtilityButton>
              </InlineItem>
            )}
          </Inline>
        ) : (
          <Inline space='small' alignY='center'>
            <InlineItem sizing='fill-container'>
              <Text tone='secondary'>
                {busy ? 'Uploading…' : 'No methodology reference.'}
              </Text>
            </InlineItem>
            {onUpload && (
              <InlineItem sizing='fit-content'>
                {uploadButton('Upload PDF')}
              </InlineItem>
            )}
          </Inline>
        )}
      </Content>
      <AnimatedPopover
        stopPropagation
        justify='start'
        open={isOpen}
        offset={4}
        placement='bottom'
        preventOverflow
        targetRef={buttonRef}
      >
        <div ref={paneRef}>
          <Content
            background='white'
            border
            borderRadiusSize='small'
            padding='medium'
            width={360}
          >
            <FileUploader
              onChange={handleFiles}
              validFileExtensions={PDF_ONLY}
              disabled={busy}
            />
          </Content>
        </div>
      </AnimatedPopover>
    </>
  );
};

export type MethodologyReferenceProps = {
  // The scenario's current document; absent = the empty slot.
  methodology?: ScenarioMethodology;
  // An upload/replace/remove is in flight — the controls hold still.
  busy?: boolean;
  // The download is in flight — the Download button spins until the signed
  // URL is minted.
  downloading?: boolean;
  // Editor-only handlers; undefined renders the viewer surface (Download
  // only, no upload affordance) — the same gating idiom as
  // `ScenarioEmissionFactorLibraries`.
  onUpload?: (file: File) => void;
  onRemove?: () => void;
  onDownload?: () => void;
};

export { MethodologyReference };
